//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useCallback } from 'react';
import { type AsyncStatusResponse } from '@/services/apiTypes';
import { asyncService } from '@/services';
import { useApiErrorHandler } from './useApiErrorHandler';

/**
 * Async-job infrastructure: poll a job's status and cancel a running job.
 * Stateless calls on the async service wrapped in the shared error handler, so
 * this hook subscribes to no React context.
 */
export function useAsyncJobs() {
  const handleError = useApiErrorHandler();

  // Will wait if wait is true for timeout(milliseconds) value
  // if timeout is > 0. If timeout is 0 it means wait forever for codes besides
  // 202. 202 means it is still processing.
  const asyncStatus = useCallback(async <R = AsyncStatusResponse>(
    asyncID: string,
    wait = true,
    timeout = 0,
    statusCallback: ((status: AsyncStatusResponse) => void) | null = null,
    abortSignal: AbortSignal | null = null,
    logoutCallback: (() => void) | null = null
  ): Promise<R> => {
    return await handleError(
      'asyncStatus',
      () => asyncService.asyncStatus<R>(asyncID, wait, timeout, statusCallback,
                                        abortSignal),
      logoutCallback);
  }, [handleError]);

  const cancelAsyncJob = useCallback(async (asyncID: string) => {
    return await handleError(
      'cancelAsyncJob', () => asyncService.cancelAsyncJob(asyncID));
  }, [handleError]);

  return { asyncStatus, cancelAsyncJob };
}
